/**
 * ThreadView
 *
 * Conversation thread display with collapsible messages, reply/forward buttons,
 * and thread-level actions.
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Star,
  Archive,
  Trash2,
  Clock,
  Tag,
  Reply,
  ReplyAll,
  Forward,
} from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MessageView } from './MessageView';
import { AIThreadSummary } from './AIThreadSummary';
import type { EmailMessage, EmailMessageDetail, EmailAttachment } from '@/lib/api';

interface ThreadViewProps {
  threadId: string;
  accountId: string;
  aiEnabled?: boolean;
  onReply: (message: EmailMessage | EmailMessageDetail) => void;
  onReplyAll: (message: EmailMessage | EmailMessageDetail) => void;
  onForward: (message: EmailMessage | EmailMessageDetail) => void;
  onToggleStar?: () => void;
  onArchive?: () => void;
  onTrash?: () => void;
  onDownloadAttachment?: (attachment: EmailAttachment) => void;
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays < 1) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface CollapsedMessageProps {
  message: EmailMessage & { attachments?: EmailAttachment[] };
  onClick: () => void;
}

function CollapsedMessage({ message, onClick }: CollapsedMessageProps) {
  return (
    <button
      className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-muted/50 transition-colors border-b border-border"
      onClick={onClick}
    >
      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
        {(message.fromName || message.fromAddress).charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">
            {message.fromName || message.fromAddress}
          </span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {formatRelativeDate(message.sentAt || message.createdAt)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground truncate">{message.snippet || ''}</p>
      </div>
    </button>
  );
}

export function ThreadView({
  threadId,
  accountId,
  aiEnabled,
  onReply,
  onReplyAll,
  onForward,
  onToggleStar,
  onArchive,
  onTrash,
  onDownloadAttachment,
}: ThreadViewProps) {
  const { data: thread, isLoading } = useQuery({
    queryKey: queryKeys.email.threadDetail(threadId),
    queryFn: () => api.getEmailThread(threadId, accountId),
    enabled: !!threadId && !!accountId,
  });

  // Track which messages are expanded — last message expanded by default
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Auto-expand last message when thread loads
  const messages = thread?.messages ?? [];
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

  const isExpanded = useCallback(
    (msgId: string) => {
      if (expandedIds.size === 0 && msgId === lastMessageId) return true;
      return expandedIds.has(msgId);
    },
    [expandedIds, lastMessageId],
  );

  const toggleExpanded = useCallback((msgId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  }, []);

  // We need message details for expanded messages — for now we cast
  // since the thread detail endpoint returns messages with attachments
  const asDetail = useCallback(
    (msg: EmailMessage & { attachments?: EmailAttachment[] }): EmailMessageDetail => ({
      ...msg,
      attachments: msg.attachments ?? [],
      labels: [],
    }),
    [],
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <div className="space-y-3 mt-6">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Thread not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Thread header */}
      <div className="px-4 py-3 border-b border-border sticky top-0 bg-background z-10">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold truncate">{thread.subject}</h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onToggleStar && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onToggleStar}>
                <Star
                  className={cn(
                    'h-4 w-4',
                    thread.isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground',
                  )}
                />
              </Button>
            )}
            {onArchive && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onArchive}
                title="Archive"
              >
                <Archive className="h-4 w-4" />
              </Button>
            )}
            {onTrash && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onTrash}
                title="Trash"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled title="Snooze">
              <Clock className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled title="Label">
              <Tag className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* AI Summary */}
      {aiEnabled && messages.length >= 3 && (
        <AIThreadSummary accountId={accountId} threadId={threadId} />
      )}

      {/* Messages */}
      <div className="flex-1">
        {messages.map((msg) => {
          const expanded = isExpanded(msg.id);

          if (!expanded) {
            return (
              <CollapsedMessage key={msg.id} message={msg} onClick={() => toggleExpanded(msg.id)} />
            );
          }

          return (
            <div key={msg.id} className="border-b border-border">
              {/* Collapse toggle */}
              <button
                className="flex items-center gap-1 px-4 pt-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => toggleExpanded(msg.id)}
              >
                <ChevronDown className="h-3 w-3" />
                <span>Collapse</span>
              </button>
              <MessageView
                message={asDetail(msg)}
                onReply={() => onReply(msg)}
                onReplyAll={() => onReplyAll(msg)}
                onForward={() => onForward(msg)}
                onDownloadAttachment={onDownloadAttachment}
              />
            </div>
          );
        })}
      </div>

      {/* Bottom reply bar for thread */}
      {messages.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-background sticky bottom-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReply(messages[messages.length - 1])}
            className="gap-1.5"
          >
            <Reply className="h-3.5 w-3.5" /> Reply
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReplyAll(messages[messages.length - 1])}
            className="gap-1.5"
          >
            <ReplyAll className="h-3.5 w-3.5" /> Reply All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onForward(messages[messages.length - 1])}
            className="gap-1.5"
          >
            <Forward className="h-3.5 w-3.5" /> Forward
          </Button>
        </div>
      )}
    </div>
  );
}
